import { ec, ecId, ecLean, activeStatuses, handBack, record, type JanusCtx, type Row } from '../ability/context';
import { carrierDefinition, layerListEntryUuid } from '../ability/statuses/status-trigger-gate';
import { readItemField } from '../compile/item-field-reader';
import { phpJsonTruthy } from '../compile/php-json-values';
import { phpJsonFloat } from '../compile/php-decimal-values';
import type { TransformSpawnRequest } from './row-plan';
import { mergeSpawnPlans } from './row-plan';
import { encounterField } from '../../shared/array-types';
import { appendTurnDeathRows } from '../death/turn-rows';
import { removeStoredStatus, storedStatusHasSpecialTeardown } from '../ability/statuses/status-effect-removal-helpers';

/** maybe_transform_creature_on_zero_vitality: first active passive, before ordinary death. */
export function selfZeroTransform(ctx: JanusCtx, target: Row): TransformSpawnRequest | null {
    for (const entry of ctx.read.entries(target, 'passive_ability')) {
        if (record(entry) && Object.prototype.hasOwnProperty.call(entry, 'passive_conditions_active')
            && !phpJsonTruthy(readItemField(record(entry), 'passive_conditions_active'))) continue;
        const definition = carrierDefinition(ctx, entry, 'spawn:self-transform');
        if (!definition) continue;
        const effect = ctx.read.value(definition, 'transform_into_creature_on_self_zero_vitality');
        const itemId = layerListEntryUuid(effect);
        if (!/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(itemId)) continue;
        const qty = record(effect) ? readItemField(record(effect), 'qty') ?? 1
            : ctx.read.value(definition, 'transform_into_creature_on_self_zero_vitality_qty') ?? 1;
        return { victimId: ecId(target), itemId, qty: Math.max(1, Math.min(10, Math.round(phpJsonFloat(qty)))),
            equipInventory: phpJsonTruthy(ctx.read.value(definition, 'transform_into_creature_on_self_zero_vitality_equip_inventory')),
            dropInventory: phpJsonTruthy(ctx.read.value(definition, 'transform_into_creature_on_self_zero_vitality_drop_inventory')),
            loot: false };
    }
    return null;
}
export function resolveSelfZeroTransform(ctx: JanusCtx, target: Row): boolean {
    const request = selfZeroTransform(ctx, target);
    if (!request) return false;
    if (!ctx.read.spawnTransform) handBack('spawn:self-transform-plan-not-held');
    if (Number(ecLean(target, 'encounter_creature_deleted') ?? 0) === 1) return true;
    const id = ecId(target), original = ctx.originals.get(id);
    if (!original) handBack('spawn:transform-before-not-held');
    if (ctx.read.inCombat(ctx.encounter, target)) {
        const holder = String(encounterField(ctx.encounter as never, 'current_encounter_creature_id_turn') ?? '').toUpperCase();
        if (!holder) handBack('spawn:transform-turn-holder-not-held');
        if (holder === id) {
            if (!ctx.read.turnDeath || ctx.turnTransition) handBack('spawn:transform-turn-transition-not-held');
            const decided = ctx.read.turnDeath(ctx.encounter, ctx.members, id);
            if (decided.kind === 'handback') handBack(`spawn:transform:${decided.reason}`);
            if (decided.kind === 'transition') ctx.turnTransition = decided.transition;
            else if (decided.kind === 'none') ctx.turnDeathNone = id;
        }
    }
    const result = ctx.read.spawnTransform!(ctx, target, original!, request);
    appendTurnDeathRows(ctx, result, id, 'spawn:transform');
    ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, result);
    ec(target).encounter_creature_dead = 1; ec(target).v_current = 0; ec(target).v_temp = 0;
    const retiring = new Set(result.writes.filter(write => write.table === 'statuses' && write.op === 'update').map(write => String(readItemField(write.row, 'status_id'))));
    for (const member of ctx.members.values()) for (const status of [...activeStatuses(ctx, member)]) {
        const statusId = String(readItemField(status, 'status_id') ?? '');
        if (!retiring.has(statusId)) continue;
        if (storedStatusHasSpecialTeardown(ctx, status)) {
            (ctx.spawnRows.logs ??= []).push({ kind: 'janus_unported_skip', name: String(ecLean(member, 'encounter_creature_name') ?? 'Creature'), detail: 'Replacement status teardown: dedicated special handler required.' });
        } else removeStoredStatus(ctx, member, status);
    }
    // Transient target statuses were never saved; existing rows retire in the plan.
    ctx.statusesAdded = ctx.statusesAdded.filter(status => status.target.toUpperCase() !== id);
    return true;
}
